import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ArchiveBoxIcon,
  TagIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

interface ConversationItem {
  id: string;
  title: string;
  messageCount: number;
  lastMessageAt: string;
  archived: boolean;
  label?: string;
  labelEmoji?: string;
  companion?: { name: string; avatarUrl?: string };
}

interface ConversationDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  companionId: string;
  currentConversationId?: string;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
}

const LABELS = [
  { emoji: '🌙', name: 'Creative' },
  { emoji: '🔮', name: 'Tarot' },
  { emoji: '📖', name: 'Story' },
  { emoji: '💜', name: 'Personal' },
  { emoji: '🧠', name: 'Research' },
  { emoji: '✍️', name: 'Writing' },
  { emoji: '🎭', name: 'Roleplay' },
  { emoji: '💡', name: 'Ideas' },
];

const ConversationDashboard: React.FC<ConversationDashboardProps> = ({
  isOpen,
  onClose,
  companionId,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
}) => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [filterLabel, setFilterLabel] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchMode, setBatchMode] = useState(false);
  const [labelingId, setLabelingId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'messages'>('recent');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  useEffect(() => {
    if (isOpen) fetchConversations();
  }, [isOpen, showArchived, filterLabel, sortBy]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { companionId, sort: sortBy };
      if (showArchived) params.archived = 'true';
      if (filterLabel) params.label = filterLabel;
      if (search) params.search = search;
      const res = await axios.get('/chat/conversations', { params });
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchConversations();

  const handleArchive = async (id: string) => {
    try {
      await axios.patch(`/chat/conversations/${id}/archive`);
      toast.success(
        showArchived
          ? t('conversations.unarchived', 'Unarchived')
          : t('conversations.archived', 'Archived'),
      );
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleSetLabel = async (id: string, label: string, emoji: string) => {
    try {
      await axios.patch(`/chat/conversations/${id}/label`, { label, labelEmoji: emoji });
      setLabelingId(null);
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleClearLabel = async (id: string) => {
    try {
      await axios.patch(`/chat/conversations/${id}/label`, { label: null, labelEmoji: null });
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleBatchArchive = async () => {
    try {
      await axios.post('/chat/conversations/batch/archive', { conversationIds: [...selectedIds] });
      toast.success(t('conversations.batchArchived', 'Archived'));
      setSelectedIds(new Set());
      setBatchMode(false);
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const handleBatchDelete = async () => {
    if (
      !window.confirm(
        t(
          'conversations.batchDeleteConfirm',
          'Delete selected conversations? This cannot be undone.',
        ),
      )
    )
      return;
    try {
      await axios.post('/chat/conversations/batch/delete', { conversationIds: [...selectedIds] });
      toast.success(t('conversations.batchDeleted', 'Deleted'));
      setSelectedIds(new Set());
      setBatchMode(false);
      fetchConversations();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return t('conversations.yesterday', 'Yesterday');
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  const usedLabels = [...new Set(conversations.map((c) => c.label).filter(Boolean))];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Panel — slides in from the LEFT, sits above chat */}
      <div
        className="fixed left-0 top-0 h-full w-80 max-w-[85vw] z-50 flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #1a1625 0%, #151221 100%)',
          borderRight: '1px solid rgba(139, 92, 246, 0.15)',
          boxShadow: '4px 0 32px rgba(0,0,0,0.6)',
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-base">💬</span>
            <h2 className="text-sm font-semibold text-white/90 tracking-wide">
              {t('conversations.title', 'Conversations')}
            </h2>
          </div>

          <div className="flex items-center gap-1">
            {/* New conversation */}
            <button
              onClick={() => {
                onNewConversation();
                onClose();
              }}
              title="New conversation"
              className="flex items-center justify-center w-7 h-7 rounded-full transition-colors"
              style={{ color: '#a78bfa' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(139,92,246,0.15)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <PlusIcon className="w-4 h-4" />
            </button>

            {/* Batch select toggle */}
            <button
              onClick={() => {
                setBatchMode(!batchMode);
                setSelectedIds(new Set());
              }}
              className="text-xs px-2 py-0.5 rounded-full transition-colors font-medium"
              style={
                batchMode
                  ? { background: '#7c3aed', color: '#fff' }
                  : { color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.1)' }
              }
            >
              {batchMode ? t('common.done', 'Done') : t('conversations.select', 'Select')}
            </button>

            {/* Close */}
            <button
              onClick={onClose}
              className="flex items-center justify-center w-7 h-7 rounded-full transition-colors"
              style={{ color: 'rgba(255,255,255,0.45)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Search ─────────────────────────────────────────── */}
        <div
          className="px-3 py-2 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
        >
          <div className="relative">
            <MagnifyingGlassIcon
              className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'rgba(255,255,255,0.35)' }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('conversations.searchPlaceholder', 'Search conversations...')}
              className="w-full pl-8 pr-3 py-1.5 rounded-lg text-xs text-white/80 placeholder-white/25 outline-none focus:ring-1"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(139,92,246,0.5)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
            />
          </div>
        </div>

        {/* ── Filters ────────────────────────────────────────── */}
        <div
          className="px-3 py-2 flex items-center gap-1.5 flex-wrap flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
        >
          {/* Archived toggle */}
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="text-[11px] px-2 py-0.5 rounded-full transition-colors font-medium"
            style={
              showArchived
                ? { background: '#7c3aed', color: '#fff' }
                : {
                    background: 'rgba(255,255,255,0.07)',
                    color: 'rgba(255,255,255,0.55)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }
            }
          >
            📦 {t('conversations.archived', 'Archived')}
          </button>

          {/* Sort */}
          <div
            className="relative"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setSortMenuOpen(false);
              }
            }}
          >
            <button
              type="button"
              onClick={() => setSortMenuOpen((open) => !open)}
              className="text-[11px] px-2 py-0.5 rounded-full outline-none cursor-pointer font-medium flex items-center gap-1 transition-colors"
              style={
                sortMenuOpen
                  ? {
                      background: 'linear-gradient(135deg,#a855f7,#6366f1)',
                      color: '#fff',
                      border: '1px solid rgba(167,139,250,0.9)',
                    }
                  : {
                      background: 'rgba(15,23,42,0.9)',
                      color: 'rgba(248,250,252,0.85)',
                      border: '1px solid rgba(148,163,184,0.45)',
                    }
              }
            >
              <span>
                {sortBy === 'recent'
                  ? t('conversations.sortRecent', 'Recent')
                  : sortBy === 'name'
                    ? t('conversations.sortName', 'Name')
                    : t('conversations.sortMessages', 'Most msgs')}
              </span>
              <span className="text-[9px] opacity-80">▾</span>
            </button>

            {sortMenuOpen && (
              <div
                className="absolute left-0 mt-1 w-32 rounded-md shadow-lg py-1 text-[11px] z-50"
                style={{
                  background: 'rgba(15,23,42,0.98)',
                  border: '1px solid rgba(15,23,42,0.8)',
                }}
              >
                <button
                  className="w-full text-left px-3 py-1.5 transition-colors"
                  style={{
                    background: sortBy === 'recent' ? 'rgba(88,28,135,0.6)' : 'transparent',
                    color: sortBy === 'recent' ? '#e9d5ff' : 'rgba(248,250,252,0.85)',
                  }}
                  onClick={() => {
                    setSortBy('recent');
                    setSortMenuOpen(false);
                  }}
                >
                  {t('conversations.sortRecent', 'Recent')}
                </button>
                <button
                  className="w-full text-left px-3 py-1.5 transition-colors"
                  style={{
                    background: sortBy === 'name' ? 'rgba(88,28,135,0.6)' : 'transparent',
                    color: sortBy === 'name' ? '#e9d5ff' : 'rgba(248,250,252,0.85)',
                  }}
                  onClick={() => {
                    setSortBy('name');
                    setSortMenuOpen(false);
                  }}
                >
                  {t('conversations.sortName', 'Name')}
                </button>
                <button
                  className="w-full text-left px-3 py-1.5 transition-colors"
                  style={{
                    background: sortBy === 'messages' ? 'rgba(88,28,135,0.6)' : 'transparent',
                    color: sortBy === 'messages' ? '#e9d5ff' : 'rgba(248,250,252,0.85)',
                  }}
                  onClick={() => {
                    setSortBy('messages');
                    setSortMenuOpen(false);
                  }}
                >
                  {t('conversations.sortMessages', 'Most msgs')}
                </button>
              </div>
            )}
          </div>

          {/* Label filters */}
          {usedLabels.map((label) => (
            <button
              key={label}
              onClick={() => setFilterLabel(filterLabel === label ? null : label!)}
              className="text-[11px] px-2 py-0.5 rounded-full transition-colors font-medium"
              style={
                filterLabel === label
                  ? { background: '#7c3aed', color: '#fff' }
                  : {
                      background: 'rgba(255,255,255,0.07)',
                      color: 'rgba(255,255,255,0.55)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }
              }
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Batch Actions ───────────────────────────────────── */}
        {batchMode && selectedIds.size > 0 && (
          <div
            className="px-3 py-1.5 flex items-center gap-2 flex-shrink-0"
            style={{
              borderBottom: '1px solid rgba(139,92,246,0.2)',
              background: 'rgba(139,92,246,0.08)',
            }}
          >
            <span className="text-[11px] text-purple-300 font-medium">
              {selectedIds.size} {t('conversations.selected', 'selected')}
            </span>
            <div className="flex-1" />
            <button
              onClick={handleBatchArchive}
              className="text-[11px] px-2 py-0.5 rounded transition-colors"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
            >
              📦 Archive
            </button>
            <button
              onClick={handleBatchDelete}
              className="text-[11px] px-2 py-0.5 rounded transition-colors"
              style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171' }}
            >
              🗑️ Delete
            </button>
          </div>
        )}

        {/* ── Conversation List ───────────────────────────────── */}
        <div
          className="flex-1 overflow-y-auto"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(139,92,246,0.3) transparent' }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div
                className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: '#7c3aed', borderTopColor: 'transparent' }}
              />
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <ChatBubbleLeftRightIcon className="w-8 h-8 opacity-20 text-white" />
              <p className="text-xs text-white/30">
                {t('conversations.empty', 'No conversations yet')}
              </p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className="relative group flex items-center gap-2.5 px-3 py-3 cursor-pointer transition-colors"
                style={{
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background:
                    conv.id === currentConversationId ? 'rgba(124,58,237,0.12)' : 'transparent',
                  borderLeft:
                    conv.id === currentConversationId
                      ? '2px solid #7c3aed'
                      : '2px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (conv.id !== currentConversationId)
                    e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                }}
                onMouseLeave={(e) => {
                  if (conv.id !== currentConversationId)
                    e.currentTarget.style.background = 'transparent';
                }}
                onClick={() => {
                  if (!batchMode) {
                    onSelectConversation(conv.id);
                    onClose();
                  }
                }}
              >
                {/* Checkbox in batch mode */}
                {batchMode && (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(conv.id)}
                    onChange={() =>
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        next.has(conv.id) ? next.delete(conv.id) : next.add(conv.id);
                        return next;
                      })
                    }
                    className="w-3.5 h-3.5 rounded flex-shrink-0 accent-violet-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}

                {/* Text content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 mb-0.5">
                    {conv.labelEmoji && (
                      <span className="text-xs leading-none">{conv.labelEmoji}</span>
                    )}
                    <p
                      className="text-sm font-medium truncate"
                      style={{ color: 'rgba(255,255,255,0.85)' }}
                    >
                      {conv.title || t('conversations.untitled', 'Untitled')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      {conv.messageCount} {t('conversations.msgs', 'msgs')}
                    </span>
                    {conv.lastMessageAt && (
                      <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
                        · {formatDate(conv.lastMessageAt)}
                      </span>
                    )}
                    {conv.label && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full"
                        style={{
                          background: 'rgba(139,92,246,0.15)',
                          color: 'rgba(167,139,250,0.8)',
                        }}
                      >
                        {conv.label}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action buttons — visible on hover */}
                {!batchMode && (
                  <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Tag */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLabelingId(labelingId === conv.id ? null : conv.id);
                      }}
                      className="flex items-center justify-center w-6 h-6 rounded transition-colors"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <TagIcon className="w-3.5 h-3.5" />
                    </button>
                    {/* Archive */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArchive(conv.id);
                      }}
                      className="flex items-center justify-center w-6 h-6 rounded transition-colors"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')
                      }
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <ArchiveBoxIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {/* Label picker popover */}
                {labelingId === conv.id && (
                  <div
                    className="absolute right-3 top-full mt-1 rounded-xl shadow-2xl p-2 z-50 grid grid-cols-4 gap-1"
                    style={{
                      background: '#1e1830',
                      border: '1px solid rgba(139,92,246,0.25)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {LABELS.map((l) => (
                      <button
                        key={l.name}
                        onClick={() => handleSetLabel(conv.id, l.name, l.emoji)}
                        title={l.name}
                        className="flex items-center justify-center w-8 h-8 rounded-lg text-lg transition-colors"
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = 'rgba(139,92,246,0.2)')
                        }
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        {l.emoji}
                      </button>
                    ))}
                    {conv.label && (
                      <button
                        onClick={() => handleClearLabel(conv.id)}
                        className="col-span-4 mt-0.5 py-1 rounded-lg text-[11px] font-medium transition-colors"
                        style={{ color: '#f87171' }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = 'rgba(239,68,68,0.1)')
                        }
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        {t('conversations.clearLabel', 'Clear label')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div
          className="px-4 py-2 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
        >
          <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.2)' }}>
            {conversations.length} {t('conversations.count', 'conversations')}
          </p>
        </div>
      </div>
    </>
  );
};

export default ConversationDashboard;
